import { motion } from 'motion/react';
import { Banknote, Boxes, Package, Settings, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { cn } from '../lib/cn';
import { useI18n, type MessageKey } from '../lib/i18n';
import { useSession } from '../lib/session';
import { haptics } from '../lib/telegram';

interface Tab {
  to: string;
  labelKey: MessageKey;
  icon: typeof Package;
  /** вкладка скрыта, если у роли нет права */
  requires?: 'finance:read' | 'orders:create';
  /** вкладка скрыта, если ни один источник так не умеет */
  needsCapability?: 'productSearch';
}

const TABS: Tab[] = [
  { to: '/orders', labelKey: 'nav.orders', icon: Package },
  { to: '/customers', labelKey: 'nav.customers', icon: Users },
  {
    to: '/products',
    labelKey: 'nav.products',
    icon: Boxes,
    requires: 'orders:create',
    needsCapability: 'productSearch',
  },
  { to: '/finance', labelKey: 'nav.finance', icon: Banknote, requires: 'finance:read' },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings },
];

/**
 * Нижняя навигация: четыре раздела, до которых дотягивается палец.
 * Вкладка «Фінанси» не показывается роли без права на деньги — лучше не
 * показать, чем показать и ответить «недостатньо прав».
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { can, stores } = useSession();
  const location = useLocation();

  const tabs = TABS.filter((tab) => {
    if (tab.requires && !can(tab.requires)) return false;
    // Вкладка без источника, который её наполняет, была бы пустой.
    if (tab.needsCapability) {
      return stores.some((store) => store.capabilities[tab.needsCapability as 'productSearch']);
    }
    return true;
  });
  const activeTab = tabs.find((tab) => location.pathname.startsWith(tab.to));

  // На карточке заказа или клиента нижнее меню прячем: там работают нативная
  // кнопка «назад» и главная кнопка Telegram, а лишняя панель только мешает.
  const isDetail = location.pathname.split('/').filter(Boolean).length > 1;

  return (
    <div className="flex min-h-dvh flex-col">
      <main
        className={cn('flex-1', !isDetail && 'pb-[calc(64px+env(safe-area-inset-bottom,0px))]')}
      >
        {children}
      </main>

      {isDetail ? null : (
        <nav
          className={cn(
            'bg-surface/92 border-line fixed inset-x-0 bottom-0 z-30 border-t backdrop-blur-xl',
            'pb-safe',
          )}
        >
          <div className="mx-auto flex max-w-lg">
            {tabs.map((tab) => {
              const isActive = activeTab?.to === tab.to;
              const Icon = tab.icon;

              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  onClick={() => haptics.tap()}
                  className="relative flex flex-1 flex-col items-center gap-1 pt-2 pb-1.5"
                >
                  {isActive && (
                    // Общий layoutId: полоска плавно переезжает между вкладками.
                    <motion.span
                      layoutId="tab-indicator"
                      className="bg-accent absolute top-0 h-[2px] w-8 rounded-full"
                      transition={{ type: 'spring', stiffness: 520, damping: 38 }}
                    />
                  )}
                  <Icon
                    size={22}
                    strokeWidth={isActive ? 2.4 : 1.8}
                    className={cn(
                      'transition-colors duration-150',
                      isActive ? 'text-accent' : 'text-muted',
                    )}
                  />
                  <span
                    className={cn(
                      'text-[11px] transition-colors duration-150',
                      isActive ? 'text-accent font-medium' : 'text-muted',
                    )}
                  >
                    {t(tab.labelKey)}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}

/** Шапка экрана: заголовок слева, действия справа, прилипает к верху. */
export function ScreenHeader({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="bg-surface/92 border-line sticky top-0 z-20 border-b backdrop-blur-xl">
      <div className="pt-safe" />
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="min-w-0">
          <h1 className="truncate text-[20px] leading-tight font-semibold">{title}</h1>
          {subtitle && <p className="text-muted truncate text-[13px]">{subtitle}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
      </div>
      {children}
    </header>
  );
}
